/* =============================================================================
   <luxury-quick-view> custom element
   - Lazy-fetches /products/{handle}?section_id=quick-view-premium
   - Slides in (right desktop, bottom-sheet mobile, swipe-down to dismiss)
   - Wires up gallery, variant picker, quantity stepper
   - <product-form> handles AJAX cart-add → cart-drawer-premium opens
   ============================================================================= */
(function () {
  'use strict';

  const SECTION_ID = 'quick-view-premium';
  const cache = new Map();

  class LuxuryQuickView extends HTMLElement {
    constructor() {
      super();
      this._onKey = this._onKey.bind(this);
      this._lastTrigger = null;
      this._touchStartY = 0;
      this._touchDeltaY = 0;
      this._dragging = false;
    }

    connectedCallback() {
      this.backdrop = this.querySelector('[data-qv-backdrop]');
      this.dialog = this.querySelector('[data-qv-dialog]');
      this.body = this.querySelector('[data-qv-body]');
      this.skeletonHTML = this.body ? this.body.innerHTML : '';
      this.closeBtn = this.querySelector('[data-qv-close]');
      this.handle = this.querySelector('.qv-panel__handle');

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-qv-close]') || e.target === this.backdrop) {
          this.close();
        }
      });

      // mobile swipe-to-dismiss on the handle
      if (this.handle) {
        this.handle.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: true });
        this.handle.addEventListener('touchmove', this._onTouchMove.bind(this), { passive: false });
        this.handle.addEventListener('touchend', this._onTouchEnd.bind(this));
      }
    }

    /* -------------------- public API -------------------- */
    async openFor(opts) {
      opts = opts || {};
      this._lastTrigger = opts.trigger || document.activeElement;

      this._show();

      const html = await this._fetchSection(opts.handle, opts.url);
      this._inject(html);

      if (opts.focusCta) {
        const addBtn = this.querySelector('[data-qvp-add]');
        if (addBtn) addBtn.focus({ preventScroll: true });
      } else if (this.dialog) {
        this.dialog.focus({ preventScroll: true });
      }
    }

    close() {
      if (this.hasAttribute('hidden')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this._onKey);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      // wait for transition end before hiding
      const onEnd = () => {
        this.setAttribute('hidden', '');
        if (this.dialog) this.dialog.removeEventListener('transitionend', onEnd);
      };
      if (this.dialog) this.dialog.addEventListener('transitionend', onEnd, { once: true });
      else this.setAttribute('hidden', '');

      // safety fallback in case transitionend doesn't fire
      setTimeout(() => this.setAttribute('hidden', ''), 700);

      if (this._lastTrigger && typeof this._lastTrigger.focus === 'function') {
        try { this._lastTrigger.focus({ preventScroll: true }); } catch (_) {}
      }
    }

    /* -------------------- internal -------------------- */
    _show() {
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      // force reflow then add class so transition runs
      // eslint-disable-next-line no-unused-expressions
      this.offsetHeight;
      requestAnimationFrame(() => this.classList.add('is-open'));
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKey);
    }

    _onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'Tab') {
        this._trapFocus(e);
      }
    }

    _trapFocus(e) {
      const focusables = this.querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }

    async _fetchSection(handle, url) {
      if (!handle && url) {
        // extract from /products/<handle>
        const m = url.match(/\/products\/([^/?#]+)/);
        if (m) handle = m[1];
      }
      if (!handle) return '';

      if (cache.has(handle)) return cache.get(handle);

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const reqUrl = root.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_ID;

      try {
        const res = await fetch(reqUrl, { headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const text = await res.text();
        cache.set(handle, text);
        return text;
      } catch (e) {
        const productUrl = root.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle);
        return '<div class="qvp qvp--error"><p>Unable to load product. <a href="' + productUrl + '">View full details</a></p></div>';
      }
    }

    _inject(html) {
      if (!this.body) return;
      // The section response is wrapped in <div id="shopify-section-...">. Use it as-is.
      this.body.innerHTML = html;
      this._wire(this.body);
    }

    _wire(scope) {
      // Gallery thumb click
      const thumbs = scope.querySelectorAll('[data-qvp-thumb]');
      const stage = scope.querySelector('[data-qvp-stage]');
      thumbs.forEach((t) => {
        t.addEventListener('click', () => {
          const i = t.getAttribute('data-index');
          thumbs.forEach((x) => x.classList.toggle('is-active', x === t));
          if (stage) {
            stage.querySelectorAll('.qvp__img').forEach((img) => {
              img.classList.toggle('is-active', img.getAttribute('data-index') === i);
            });
          }
        });
      });

      // Variant data
      const dataNode = scope.querySelector('[data-qvp-variants]');
      let variants = [];
      try { variants = dataNode ? JSON.parse(dataNode.textContent) : []; } catch (_) {}
      const variantInput = scope.querySelector('[data-qvp-variant-id]');
      const priceEl = scope.querySelector('[data-qvp-price]');
      const addBtn = scope.querySelector('[data-qvp-add]');
      const addLabel = scope.querySelector('.qvp__add-label');

      const optionInputs = scope.querySelectorAll('[data-qvp-option-input]');

      const updateFromSelection = () => {
        const selected = [];
        const optionGroups = {};
        optionInputs.forEach((inp) => {
          const pos = inp.getAttribute('data-option-position');
          optionGroups[pos] = optionGroups[pos] || [];
          optionGroups[pos].push(inp);
          if (inp.checked) selected[Number(pos) - 1] = inp.value;
        });

        // Update current-value display
        Object.keys(optionGroups).forEach((pos) => {
          const checked = optionGroups[pos].find((i) => i.checked);
          const lbl = scope.querySelector('[data-qvp-opt-current="' + pos + '"]');
          if (lbl && checked) lbl.textContent = checked.value;
        });

        const match = variants.find((v) =>
          (v.options || []).every((opt, idx) => opt === selected[idx])
        );

        if (!match) {
          if (addBtn) {
            addBtn.disabled = true;
            if (addLabel) addLabel.textContent = 'Unavailable';
          }
          return;
        }

        if (variantInput) variantInput.value = match.id;

        if (addBtn) {
          addBtn.disabled = !match.available;
          if (addLabel) addLabel.textContent = match.available ? 'Add to cart' : 'Sold out';
        }

        if (priceEl) {
          if (match.compare_at_price && match.compare_at_price > match.price) {
            priceEl.innerHTML =
              '<span class="qvp__price-sale">' + formatMoney(match.price) + '</span>' +
              '<s class="qvp__price-compare">' + formatMoney(match.compare_at_price) + '</s>';
          } else {
            priceEl.innerHTML = '<span class="qvp__price-regular">' + formatMoney(match.price) + '</span>';
          }
        }
      };

      optionInputs.forEach((inp) => inp.addEventListener('change', updateFromSelection));

      // Quantity stepper
      const qInput = scope.querySelector('[data-qvp-qty-input]');
      scope.querySelectorAll('[data-qvp-qty]').forEach((b) => {
        b.addEventListener('click', () => {
          if (!qInput) return;
          const delta = parseInt(b.getAttribute('data-qvp-qty'), 10) || 0;
          const next = Math.max(1, (parseInt(qInput.value, 10) || 1) + delta);
          qInput.value = next;
        });
      });

      // Listen for cart add (product-form fires no event, but we observe the button submit)
      const form = scope.querySelector('.qvp__form');
      if (form) {
        form.addEventListener('submit', () => {
          if (addBtn) addBtn.classList.add('is-loading');
        });
      }

      // Subscribe to cartUpdate to close panel + show toast
      if (window.subscribe && window.PUB_SUB_EVENTS) {
        if (this._unsub) try { this._unsub(); } catch (_) {}
        this._unsub = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (payload) => {
          if (!payload) return;
          if (addBtn) addBtn.classList.remove('is-loading');
          // Only react if source is product-form (cart-drawer-premium opens itself)
          if (window.LuxCard && window.LuxCard.showToast) {
            window.LuxCard.showToast('Added to your cart', 'success');
          }
          this.close();
        });
      }
    }

    /* -------------------- swipe down (mobile) -------------------- */
    _onTouchStart(e) {
      if (!e.touches || !e.touches.length) return;
      this._touchStartY = e.touches[0].clientY;
      this._touchDeltaY = 0;
      this._dragging = true;
      if (this.dialog) this.dialog.style.transition = 'none';
    }
    _onTouchMove(e) {
      if (!this._dragging || !e.touches || !e.touches.length) return;
      const dy = e.touches[0].clientY - this._touchStartY;
      if (dy <= 0) return;
      this._touchDeltaY = dy;
      e.preventDefault();
      if (this.dialog) this.dialog.style.transform = 'translate3d(0,' + dy + 'px,0)';
    }
    _onTouchEnd() {
      if (!this._dragging) return;
      this._dragging = false;
      if (this.dialog) {
        this.dialog.style.transition = '';
        this.dialog.style.transform = '';
      }
      if (this._touchDeltaY > 120) this.close();
      this._touchDeltaY = 0;
    }
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    const v = (cents / 100).toFixed(2);
    return '$' + v;
  }

  if (!customElements.get('luxury-quick-view')) {
    customElements.define('luxury-quick-view', LuxuryQuickView);
  }
})();
